/**
 * Gaze (มองตาม): a standing or ready frame with the eyes, and a step of the head, turned toward
 * someone. A gaze is one of a few codes (eyes left/centre/right × up/level/down, the head a step
 * left, straight or right), drawn as a figure's own `extra('gaze', ph)` frames and cached like
 * every other frame: following someone with the eyes never redraws anything per frame.
 *
 * Directions are in the frame's own space (+x to the right of the unmirrored frame); the renderer
 * flips them for a mirrored figure. Only the traced face features (head2.ts, face2.ts) move their eyes; the head
 * step moves the head joint (and with it the skull, hair and headwear) in the turned views, and the
 * features a pixel across the face in every view that shows them.
 */
import { FRAMES, type Pose } from '../sprites';
import type { FineFace, FineSprite } from './head2';
import type { Skeleton } from './skeleton';

export interface Gaze {
  /** Eyes: −1 left, 0 ahead, 1 right (frame space). */
  x: -1 | 0 | 1;
  /** Eyes: −1 up, 0 level, 1 down. */
  y: -1 | 0 | 1;
  /** The head a step toward −1 left or 1 right (the eyes always lead it the same way). */
  head: -1 | 0 | 1;
}

/** The poses a gaze is drawn on: standing at ease and the ready stance, each cycling `FRAMES` frames. */
export const GAZE_BASES = ['idle', 'ready'] as const satisfies readonly Pose[];
export type GazeBase = (typeof GAZE_BASES)[number];

/** Gaze as one small number, 0–26 (13 is straight ahead). */
export const gazeCode = (g: Gaze): number => g.x + 1 + 3 * (g.y + 1) + 9 * (g.head + 1);
export const GAZE_AHEAD = 13;

const sgn = (n: number) => (n < 0 ? -1 : n > 0 ? 1 : 0) as -1 | 0 | 1;

/** The `ph` of a gaze frame: which gaze, on which pose, at which frame of its cycle. */
export function gazePhase(code: number, base: GazeBase, frame: number): number {
  return (
    (code * GAZE_BASES.length + GAZE_BASES.indexOf(base)) * FRAMES + (((frame % FRAMES) + FRAMES) % FRAMES)
  );
}

export function decodeGaze(ph: number): { gaze: Gaze; base: GazeBase; frame: number } {
  const frame = ph % FRAMES;
  const rest = Math.floor(ph / FRAMES);
  const base = GAZE_BASES[rest % GAZE_BASES.length]!;
  const code = Math.floor(rest / GAZE_BASES.length) % 27;
  return {
    gaze: {
      x: sgn((code % 3) - 1),
      y: sgn((Math.floor(code / 3) % 3) - 1),
      head: sgn(Math.floor(code / 9) - 1),
    },
    base,
    frame,
  };
}

/**
 * The skeleton of a gaze frame: the base pose's own skeleton (so it breathes and stands exactly as
 * that pose does), the head joint stepped a pixel across in the turned views, and the gaze noted for
 * the face layer.
 */
export function gazeSkeleton(ph: number, make: (pose: Pose, ph: number) => Skeleton): Skeleton {
  const { gaze, base, frame } = decodeGaze(ph);
  const sk = make(base, frame);
  if (gaze.head && !sk.square) sk.joints.head = { ...sk.joints.head, x: sk.joints.head.x + gaze.head };
  return { ...sk, gaze };
}

// ── the eyes ──

const rgb = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number];

/**
 * The eyeball's own pixels: whites and their grey shading, neither skin (warm, red over blue) nor
 * lash-dark. The iris lies between them on each row and column.
 */
function isWhite(hex: string): boolean {
  const [r, g, b] = rgb(hex);
  const lum = r * 0.3 + g * 0.59 + b * 0.11;
  return lum >= 110 && Math.max(r, g, b) - Math.min(r, g, b) < 40;
}

const SHIFTED = new WeakMap<FineSprite, Map<number, FineSprite>>();

/**
 * A traced eye sprite with its irises moved `dx`, `dy` canvas pixels inside the eye: on each row
 * (then each column) of each eye, the pixels between the first and last white slide over, the
 * corners staying where they are; the white behind the iris fills in from its neighbour and iris
 * slid under a lid is hidden by it. `head` slides the whole sprite (a head turned a step). Cached.
 */
export function gazeEyes(sp: FineSprite, dx: number, dy: number, head = 0): FineSprite {
  if (!dx && !dy && !head) return sp;
  const key = dx + 1 + 3 * (dy + 1) + 9 * (head + 1);
  let byKey = SHIFTED.get(sp);
  if (!byKey) SHIFTED.set(sp, (byKey = new Map()));
  const hit = byKey.get(key);
  if (hit) return hit;
  const white = [...sp.palette].map(isWhite);
  const KEYS = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
  const isW = (ch: string) => ch !== '.' && !!white[KEYS.indexOf(ch)];
  let grid = sp.rows.map((r) => [...r]);
  /** Slides each run of eye along one line by `d`, keeping its two end pixels. */
  const slide = (line: string[], d: number): string[] => {
    if (!d) return line;
    const out = [...line];
    let c = 0;
    while (c < line.length) {
      if (!isW(line[c]!)) {
        c++;
        continue;
      }
      // One eye's run: from this white to the last white before a gap ('.').
      let end = c;
      for (let k = c; k < line.length && line[k] !== '.'; k++) if (isW(line[k]!)) end = k;
      // The corners stay; between them the eye slides, white filling in from the corner it left.
      if (end - c >= 2)
        for (let k = c + 1; k < end; k++) {
          const from = k - d;
          out[k] = from > c && from < end ? line[from]! : line[d > 0 ? c : end]!;
        }
      c = end + 1;
    }
    return out;
  };
  grid = grid.map((row) => slide(row, dx));
  if (dy) {
    const h = grid.length;
    const w = Math.max(...grid.map((r) => r.length));
    for (let x = 0; x < w; x++) {
      const col = slide(
        grid.map((r) => r[x] ?? '.'),
        dy,
      );
      for (let y = 0; y < h; y++) if (grid[y]![x] !== undefined) grid[y]![x] = col[y]!;
    }
  }
  const out = { ...sp, x: sp.x + head, rows: grid.map((r) => r.join('')) };
  byKey.set(key, out);
  return out;
}

const FACES = new WeakMap<FineFace, Map<number, FineFace>>();

/**
 * A traced face (head2.ts / face2.ts) turned by a gaze: every feature a canvas pixel across for a
 * head step, the open eyes' irises moved inside them. Cached per face and gaze.
 */
export function gazeFace(face: FineFace | undefined, gaze: Gaze | undefined): FineFace | undefined {
  if (!face || !gaze || gazeCode(gaze) === GAZE_AHEAD) return face;
  const code = gazeCode(gaze);
  let byCode = FACES.get(face);
  if (!byCode) FACES.set(face, (byCode = new Map()));
  const hit = byCode.get(code);
  if (hit) return hit;
  const step = (sp: FineSprite | undefined) => sp && (gaze.head ? { ...sp, x: sp.x + gaze.head } : sp);
  const out: FineFace = {
    eyes: face.eyes && gazeEyes(face.eyes, gaze.x, gaze.y, gaze.head),
    closed: step(face.closed),
    brows: step(face.brows),
    nose: step(face.nose),
    mouth: step(face.mouth),
  };
  byCode.set(code, out);
  return out;
}
